/** Internal type. DO NOT USE DIRECTLY. */
type Exact<T extends { [key: string]: unknown }> = { [K in keyof T]: T[K] };
/** Internal type. DO NOT USE DIRECTLY. */
export type Incremental<T> =
  | T
  | {
      [P in keyof T]?: P extends " $fragmentName" | "__typename" ? T[P] : never;
    };

import type { GraphQLClient, RequestOptions } from "graphql-request";
import gql from "graphql-tag";
import type * as Types from "./schema-types";

export * from "./schema-types";

type GraphQLClientRequestHeaders = RequestOptions["requestHeaders"];
export type GetActivityQueryVariables = Exact<{
  after?: string | null | undefined;
}>;

export type GetActivityQuery = {
  activity: {
    pageInfo: {
      hasPreviousPage: boolean;
      hasNextPage: boolean;
      startCursor: string;
      endCursor: string;
    };
    items: Array<
      | {
          __typename: "CreditExpiredActivity";
          id: string;
          title: string;
          timestamp: string;
          locationName: string | null;
          credit: {
            id: string;
            title: string;
            amount: number;
            remaining: number;
            created: string;
            expires: string;
          };
        }
      | {
          __typename: "ItemExpiredActivity";
          id: string;
          title: string;
          timestamp: string;
          locationName: string | null;
          item: {
            id: string | null;
            name: string | null;
            expires: string | null;
            claimed: string | null;
            claimedLocation: string | null;
            imageUrl: string | null;
          };
        }
      | {
          __typename: "MessageActivity";
          body: string;
          id: string;
          title: string;
          timestamp: string;
          locationName: string | null;
        }
      | {
          __typename: "PointsActivity";
          points: number;
          pointsText: string;
          id: string;
          title: string;
          timestamp: string;
          locationName: string | null;
        }
      | {
          __typename: "PromotionActivity";
          points: number;
          pointsText: string;
          id: string;
          title: string;
          timestamp: string;
          locationName: string | null;
          promotion: {
            title: string;
            details: string;
            price: number | null;
            priceType: Types.PromotionPriceType | null;
          };
          promotionItem: {
            name: string | null;
            expires: string | null;
            claimed: string | null;
            claimedLocation: string | null;
            imageUrl: string | null;
          } | null;
          promotionOrder: {
            total: number;
            tax: number | null;
            itemTotal: number;
            itemTax: number | null;
            refId: string;
            id: string;
            billingType: string | null;
            tipAmount: number | null;
            creditAmount: number;
            customerFeeAmount: number;
            currency: string;
            serviceRef: string | null;
            event: { tone: Types.Tone; message: string };
            deliveryAddress: {
              apartment: string | null;
              streetAddress: string;
              locality: string;
              region: string;
              country: string;
              postcode: string;
              coords: { lat: number; lng: number };
            } | null;
            items: Array<{
              amount: number;
              tax: number | null;
              name: string;
              quantity: number;
              options: Array<{
                name: string;
                choices: Array<{
                  amount: number;
                  name: string;
                  tax: number | null;
                } | null>;
              } | null> | null;
            }>;
            discounts: Array<{
              amount: number;
              tax: number | null;
              name: string;
            } | null> | null;
          } | null;
          promotionCredit: {
            id: string;
            title: string;
            amount: number;
            remaining: number;
            created: string;
            expires: string;
          } | null;
        }
      | {
          __typename: "SpendActivity";
          pointsText: string;
          points: number;
          id: string;
          title: string;
          timestamp: string;
          locationName: string;
          order: {
            total: number;
            tax: number | null;
            itemTotal: number;
            itemTax: number | null;
            refId: string;
            id: string;
            billingType: string | null;
            tipAmount: number | null;
            creditAmount: number;
            customerFeeAmount: number;
            externalAmount: number;
            currency: string;
            paymentId: string | null;
            paymentMethodType: string | null;
            paymentMethodName: string | null;
            paymentMethodIconUrl: string | null;
            serviceRef: string | null;
            fulfillAt: string | null;
            estimatedReadyAt: string | null;
            deliveryAtMin: string | null;
            deliveryAtMax: string | null;
            event: { tone: Types.Tone; message: string };
            deliveryAddress: {
              apartment: string | null;
              streetAddress: string;
              locality: string;
              region: string;
              country: string;
              postcode: string;
              coords: { lat: number; lng: number };
            } | null;
            items: Array<{
              amount: number;
              tax: number | null;
              name: string;
              quantity: number;
              options: Array<{
                name: string;
                choices: Array<{
                  amount: number;
                  name: string;
                  tax: number | null;
                } | null>;
              } | null> | null;
            }>;
            discounts: Array<{
              amount: number;
              tax: number | null;
              name: string;
            } | null> | null;
          };
          cart: {
            locationId: string;
            locationRef: string;
            timestamp: string;
            notes: string | null;
            items: Array<{
              id: string;
              note: string | null;
              quantity: number;
              options: Array<{
                id: string;
                choices: Array<{
                  id: string;
                  quantity: number;
                  options: Array<{
                    id: string;
                    choices: Array<{
                      id: string;
                      quantity: number;
                      options: Array<{
                        id: string;
                        choices: Array<{ id: string; quantity: number }>;
                      }>;
                    }>;
                  }>;
                }>;
              }>;
            }>;
          } | null;
        }
    >;
  };
};

export type GetActivitySummaryQueryVariables = Exact<{ [key: string]: never }>;

export type GetActivitySummaryQuery = {
  activity: { summary: { points: number; credit: number; itemCount: number } };
};

export type GetAddressesQueryVariables = Exact<{
  query: string;
  locationRef?: string | null | undefined;
  sessionToken?: string | null | undefined;
}>;

export type GetAddressesQuery = {
  addressAutocomplete: Array<{ name: string; ref: string }>;
};

export type GetAddressDetailsQueryVariables = Exact<{
  ref: string;
  sessionToken: string;
}>;

export type GetAddressDetailsQuery = {
  addressDetails: {
    details: {
      name: string;
      streetAddress: string;
      locality: string;
      postcode: string;
      region: string;
      country: string;
      coords: { lat: number; lng: number };
    } | null;
    error: { message: string } | null;
  };
};

export type FindPlaceQueryVariables = Exact<{
  query: string;
}>;

export type FindPlaceQuery = {
  findPlace: {
    name: string;
    formattedAddress: string;
    lat: number;
    lng: number;
  } | null;
};

export type GetPromptAppReviewQueryVariables = Exact<{ [key: string]: never }>;

export type GetPromptAppReviewQuery = { promptAppReview: boolean };

export type SubmitAppReviewMutationVariables = Exact<{
  score: Types.AppScore;
  deviceId?: string | null | undefined;
}>;

export type SubmitAppReviewMutation = {
  submitAppReview: { error: { message: string } | null };
};

export type CreateCartMutationVariables = Exact<{
  id: string;
  locationRef: string;
  items: Array<Types.CartItemInput> | Types.CartItemInput;
  fulfillmentType: Types.FulfillmentType;
  notes?: string | null | undefined;
  deliveryAddress?: Types.DeliveryAddress | null | undefined;
  serviceRef?: string | null | undefined;
  timeSlot?: string | null | undefined;
  tags?: Array<string> | string | null | undefined;
}>;

export type CreateCartMutation = {
  createCart: {
    adjustments: Array<{
      id: string;
      label: string;
      amount: number;
      taxIncluded: number | null;
      itemsAffected: Array<{ index: number; quantity: number }> | null;
    }> | null;
    discounts: Array<{
      id: string;
      name: string;
      discountAmount: number;
      discountTax: number | null;
      itemId: string | null;
      rate: number;
    }> | null;
    error: { message: string } | null;
  };
};

export type RedeemCodeMutationVariables = Exact<{
  code: string;
}>;

export type RedeemCodeMutation = {
  redeemCode: {
    error: { message: string } | null;
    messageDetails:
      | { title: string; message: string }
      | {
          rewardType: Types.PromotionRewardType;
          title: string;
          message: string;
        }
      | null;
  };
};

export type GetCreditQueryVariables = Exact<{
  location: string;
}>;

export type GetCreditQuery = { balance: { credit: number | null } };

export type BaseProductDefFieldsFragment = { id: string; title: string | null };

export type BaseProductOptionDefFieldsFragment = {
  id: string;
  title: string | null;
  maxQuantity: number | null;
  minQuantity: number;
};

export type BaseOptionChoiceDefFieldsFragment = {
  defaultQuantity: number;
  maxQuantity: number;
  minQuantity: number;
};

export type GetLocationDealsQueryVariables = Exact<{
  locationRef: string;
}>;

export type GetLocationDealsQuery = {
  locationDeals: Array<{
    id: string;
    title: string;
    plu: string;
    hidden: boolean;
    exclusive: boolean;
    description: string | null;
    imageUrl: string | null;
    startAt: string | null;
    endAt: string | null;
    fulfillmentType: Array<Types.FulfillmentType> | null;
    options: Array<{
      id: string;
      title: string;
      maxQuantity: number | null;
      minQuantity: number;
      choices: Array<{
        defaultQuantity: number;
        maxQuantity: number;
        minQuantity: number;
        productDef: {
          id: string;
          title: string | null;
          options: Array<{
            id: string;
            title: string | null;
            maxQuantity: number | null;
            minQuantity: number;
            choices: Array<{
              defaultQuantity: number;
              maxQuantity: number;
              minQuantity: number;
              productDef: {
                id: string;
                title: string | null;
                options: Array<{
                  id: string;
                  title: string | null;
                  maxQuantity: number | null;
                  minQuantity: number;
                  choices: Array<{
                    defaultQuantity: number;
                    maxQuantity: number;
                    minQuantity: number;
                    productDef: {
                      id: string;
                      title: string | null;
                      options: Array<{
                        id: string;
                        title: string | null;
                        maxQuantity: number | null;
                        minQuantity: number;
                        choices: Array<{
                          defaultQuantity: number;
                          maxQuantity: number;
                          minQuantity: number;
                          productDef: {
                            id: string;
                            title: string | null;
                            options: Array<{
                              id: string;
                              title: string | null;
                              maxQuantity: number | null;
                              minQuantity: number;
                              choices: Array<{
                                defaultQuantity: number;
                                maxQuantity: number;
                                minQuantity: number;
                                productDef: {
                                  id: string;
                                  title: string | null;
                                };
                              }> | null;
                            }> | null;
                          };
                        }> | null;
                      }> | null;
                    };
                  }> | null;
                }> | null;
              };
            }> | null;
          }> | null;
        };
      }> | null;
    }>;
    availability: Array<{
      dayOfTheWeek: Types.DayOfTheWeek;
      start: string;
      end: string;
    }> | null;
  }>;
};

export type GetFeaturesQueryVariables = Exact<{
  keys: Array<string> | string;
  deviceId?: string | null | undefined;
  location?: string | null | undefined;
}>;

export type GetFeaturesQuery = {
  features: Array<{ key: string; enabled: boolean }>;
};

export type GetLanguageStringsQueryVariables = Exact<{ [key: string]: never }>;

export type GetLanguageStringsQuery = {
  strings: {
    webOrderPickupName: string | null;
    webOrderDeliveryName: string | null;
    webOrderServiceName: string | null;
    webChooseLocationTitle: string | null;
    serviceChargeTitle: string;
    serviceChargeBody: string;
    serviceChargeApply: string;
    serviceChargeRemove: string;
    webServiceRefLabel: string;
    webPlaceRefLabel: string;
    webExternalPaymentPickUpButton: string;
    webExternalPaymentPickUpMessage: string;
    webExternalPaymentDeliveryButton: string;
    webExternalPaymentDeliveryMessage: string;
    webExternalPaymentServiceButton: string;
    webExternalPaymentServiceMessage: string;
  } | null;
};

export type GetLocationOrderingEnabledQueryVariables = Exact<{
  locationRef: string;
}>;

export type GetLocationOrderingEnabledQuery = {
  location: {
    orderAheadEnabled: boolean;
    orderAtTableEnabled: boolean;
    orderDeliveryEnabled: boolean;
  } | null;
};

export type GetLocationsQueryVariables = Exact<{ [key: string]: never }>;

export type GetLocationsQuery = {
  merchant: { name: string };
  locations: Array<{
    id: string;
    slug: string | null;
    name: string;
    phoneNumber: string | null;
    openingHours: string | null;
    website: string | null;
    orderAheadEnabled: boolean;
    orderAtTableEnabled: boolean;
    orderDeliveryEnabled: boolean;
    timezone: string;
    address: {
      street: string | null;
      locality: string | null;
      region: string | null;
      postalCode: string | null;
      country: string | null;
    } | null;
    coords: { latitude: number; longitude: number } | null;
    social: {
      facebook: string | null;
      twitter: string | null;
      instagram: string | null;
    } | null;
  }> | null;
};

export type GetLoginConfigQueryVariables = Exact<{ [key: string]: never }>;

export type GetLoginConfigQuery = {
  merchant: {
    googleClientId: string | null;
    appleWebClientId: string | null;
    settings: { loginProviders: Array<Types.LoginProvider> };
  };
};

export type GetMerchantQueryVariables = Exact<{ [key: string]: never }>;

export type GetMerchantQuery = {
  merchant: {
    uid: string;
    androidStoreUrl: string | null;
    iosStoreUrl: string | null;
    currency: string;
    settings: { termsAndConditionsURL: string };
  };
};

export type GetMerchantRequiresPhoneQueryVariables = Exact<{
  [key: string]: never;
}>;

export type GetMerchantRequiresPhoneQuery = {
  merchant: { settings: { requirePhoneWeb: boolean } };
};

export type CreateOrderMutationVariables = Exact<{
  id: string;
  locationRef: string;
  total: number;
  items: Array<Types.OrderAheadItem> | Types.OrderAheadItem;
  notes?: string | null | undefined;
  deliveryAddress?: Types.DeliveryAddress | null | undefined;
  serviceRef?: string | null | undefined;
  tip?: number | null | undefined;
  timeSlot?: string | null | undefined;
  customerOverride?: Types.OrderCustomerOverride | null | undefined;
}>;

export type CreateOrderMutation = {
  createOrder: {
    session: {
      id: string;
      currency: string;
      paymentIntentSecret: string | null;
      creditAmount: number;
      discountAmount: number;
      paymentAmount: number;
      requirePhoneVerification: boolean | null;
      token: string | null;
      summaryItems: Array<{
        label: string;
        summaryItemType: Types.SummaryItemType;
        amount: number;
        taxIncluded: number | null;
      }>;
    } | null;
    error: { message: string } | null;
  };
};

export type CreateOrderFromCartMutationVariables = Exact<{
  cartId: string;
  locationRef: string;
  tip?: number | null | undefined;
  customerOverride?: Types.OrderCustomerOverride | null | undefined;
}>;

export type CreateOrderFromCartMutation = {
  createOrderFromCart: {
    session: {
      id: string;
      currency: string;
      paymentIntentSecret: string | null;
      creditAmount: number;
      discountAmount: number;
      paymentAmount: number;
      requirePhoneVerification: boolean | null;
      token: string | null;
      summaryItems: Array<{
        label: string;
        summaryItemType: Types.SummaryItemType;
        amount: number;
        taxIncluded: number | null;
      }>;
    } | null;
    error: { message: string } | null;
  };
};

export type UpdateOrderTipMutationVariables = Exact<{
  orderId: string;
  tip: number;
}>;

export type UpdateOrderTipMutation = {
  updateOrderTip: { error: { message: string } | null };
};

export type CompleteOrderMutationVariables = Exact<{
  orderId: string;
  externalPayment?: boolean | null | undefined;
}>;

export type CompleteOrderMutation = {
  completeOrder: { newCustomer: boolean; error: { message: string } | null };
};

export type CancelOrderMutationVariables = Exact<{
  orderId: string;
}>;

export type CancelOrderMutation = {
  cancelOrder: { error: { message: string } | null };
};

export type BaseProductFieldsFragment = {
  id: string;
  plu: string | null;
  title: string;
  description: string | null;
  imageUrl: string | null;
  available: boolean;
  tags: Array<string>;
  visibility: Array<{
    start: string;
    end: string;
    dayOfTheWeek: Types.DayOfTheWeek;
  }> | null;
  price: { amount: number; taxIncluded: number | null };
};

export type DeepProductFieldsFragment = {
  id: string;
  plu: string | null;
  title: string;
  description: string | null;
  imageUrl: string | null;
  available: boolean;
  tags: Array<string>;
  options: Array<{
    id: string;
    title: string;
    minQuantity: number;
    maxQuantity: number | null;
    choices: Array<{
      minQuantity: number;
      maxQuantity: number;
      defaultQuantity: number;
      product: {
        id: string;
        plu: string | null;
        title: string;
        description: string | null;
        imageUrl: string | null;
        available: boolean;
        tags: Array<string>;
        options: Array<{
          id: string;
          title: string;
          minQuantity: number;
          maxQuantity: number | null;
          choices: Array<{
            minQuantity: number;
            maxQuantity: number;
            defaultQuantity: number;
            product: {
              id: string;
              plu: string | null;
              title: string;
              description: string | null;
              imageUrl: string | null;
              available: boolean;
              tags: Array<string>;
              options: Array<{
                id: string;
                title: string;
                minQuantity: number;
                maxQuantity: number | null;
                choices: Array<{
                  minQuantity: number;
                  maxQuantity: number;
                  defaultQuantity: number;
                  product: {
                    id: string;
                    plu: string | null;
                    title: string;
                    description: string | null;
                    imageUrl: string | null;
                    available: boolean;
                    tags: Array<string>;
                    options: Array<{
                      id: string;
                      title: string;
                      minQuantity: number;
                      maxQuantity: number | null;
                      choices: Array<{
                        minQuantity: number;
                        maxQuantity: number;
                        defaultQuantity: number;
                        product: {
                          id: string;
                          plu: string | null;
                          title: string;
                          description: string | null;
                          imageUrl: string | null;
                          available: boolean;
                          tags: Array<string>;
                          options: Array<{
                            id: string;
                            title: string;
                            minQuantity: number;
                            maxQuantity: number | null;
                            choices: Array<{
                              minQuantity: number;
                              maxQuantity: number;
                              defaultQuantity: number;
                              product: {
                                id: string;
                                plu: string | null;
                                title: string;
                                description: string | null;
                                imageUrl: string | null;
                                available: boolean;
                                tags: Array<string>;
                                visibility: Array<{
                                  start: string;
                                  end: string;
                                  dayOfTheWeek: Types.DayOfTheWeek;
                                }> | null;
                                price: {
                                  amount: number;
                                  taxIncluded: number | null;
                                };
                              };
                            }>;
                          }>;
                          visibility: Array<{
                            start: string;
                            end: string;
                            dayOfTheWeek: Types.DayOfTheWeek;
                          }> | null;
                          price: { amount: number; taxIncluded: number | null };
                        };
                      }>;
                    }>;
                    visibility: Array<{
                      start: string;
                      end: string;
                      dayOfTheWeek: Types.DayOfTheWeek;
                    }> | null;
                    price: { amount: number; taxIncluded: number | null };
                  };
                }>;
              }>;
              visibility: Array<{
                start: string;
                end: string;
                dayOfTheWeek: Types.DayOfTheWeek;
              }> | null;
              price: { amount: number; taxIncluded: number | null };
            };
          }>;
        }>;
        visibility: Array<{
          start: string;
          end: string;
          dayOfTheWeek: Types.DayOfTheWeek;
        }> | null;
        price: { amount: number; taxIncluded: number | null };
      };
    }>;
  }>;
  visibility: Array<{
    start: string;
    end: string;
    dayOfTheWeek: Types.DayOfTheWeek;
  }> | null;
  price: { amount: number; taxIncluded: number | null };
};

export type GetMenusQueryVariables = Exact<{
  location: string;
  dispatchOption?: Types.FulfillmentType | null | undefined;
  productIds?: Array<string> | string | null | undefined;
}>;

export type GetMenusQuery = {
  ordering: {
    menus: Array<{
      id: string;
      title: string;
      fulfillmentType: Array<Types.FulfillmentType> | null;
      categories: Array<{
        id: string;
        title: string;
        products: Array<{
          id: string;
          plu: string | null;
          title: string;
          description: string | null;
          imageUrl: string | null;
          available: boolean;
          tags: Array<string>;
          options: Array<{
            id: string;
            title: string;
            minQuantity: number;
            maxQuantity: number | null;
            choices: Array<{
              minQuantity: number;
              maxQuantity: number;
              defaultQuantity: number;
              product: {
                id: string;
                plu: string | null;
                title: string;
                description: string | null;
                imageUrl: string | null;
                available: boolean;
                tags: Array<string>;
                options: Array<{
                  id: string;
                  title: string;
                  minQuantity: number;
                  maxQuantity: number | null;
                  choices: Array<{
                    minQuantity: number;
                    maxQuantity: number;
                    defaultQuantity: number;
                    product: {
                      id: string;
                      plu: string | null;
                      title: string;
                      description: string | null;
                      imageUrl: string | null;
                      available: boolean;
                      tags: Array<string>;
                      options: Array<{
                        id: string;
                        title: string;
                        minQuantity: number;
                        maxQuantity: number | null;
                        choices: Array<{
                          minQuantity: number;
                          maxQuantity: number;
                          defaultQuantity: number;
                          product: {
                            id: string;
                            plu: string | null;
                            title: string;
                            description: string | null;
                            imageUrl: string | null;
                            available: boolean;
                            tags: Array<string>;
                            options: Array<{
                              id: string;
                              title: string;
                              minQuantity: number;
                              maxQuantity: number | null;
                              choices: Array<{
                                minQuantity: number;
                                maxQuantity: number;
                                defaultQuantity: number;
                                product: {
                                  id: string;
                                  plu: string | null;
                                  title: string;
                                  description: string | null;
                                  imageUrl: string | null;
                                  available: boolean;
                                  tags: Array<string>;
                                  options: Array<{
                                    id: string;
                                    title: string;
                                    minQuantity: number;
                                    maxQuantity: number | null;
                                    choices: Array<{
                                      minQuantity: number;
                                      maxQuantity: number;
                                      defaultQuantity: number;
                                      product: {
                                        id: string;
                                        plu: string | null;
                                        title: string;
                                        description: string | null;
                                        imageUrl: string | null;
                                        available: boolean;
                                        tags: Array<string>;
                                        visibility: Array<{
                                          start: string;
                                          end: string;
                                          dayOfTheWeek: Types.DayOfTheWeek;
                                        }> | null;
                                        price: {
                                          amount: number;
                                          taxIncluded: number | null;
                                        };
                                      };
                                    }>;
                                  }>;
                                  visibility: Array<{
                                    start: string;
                                    end: string;
                                    dayOfTheWeek: Types.DayOfTheWeek;
                                  }> | null;
                                  price: {
                                    amount: number;
                                    taxIncluded: number | null;
                                  };
                                };
                              }>;
                            }>;
                            visibility: Array<{
                              start: string;
                              end: string;
                              dayOfTheWeek: Types.DayOfTheWeek;
                            }> | null;
                            price: {
                              amount: number;
                              taxIncluded: number | null;
                            };
                          };
                        }>;
                      }>;
                      visibility: Array<{
                        start: string;
                        end: string;
                        dayOfTheWeek: Types.DayOfTheWeek;
                      }> | null;
                      price: { amount: number; taxIncluded: number | null };
                    };
                  }>;
                }>;
                visibility: Array<{
                  start: string;
                  end: string;
                  dayOfTheWeek: Types.DayOfTheWeek;
                }> | null;
                price: { amount: number; taxIncluded: number | null };
              };
            }>;
          }>;
          visibility: Array<{
            start: string;
            end: string;
            dayOfTheWeek: Types.DayOfTheWeek;
          }> | null;
          price: { amount: number; taxIncluded: number | null };
        }> | null;
      }> | null;
      serviceAvailability: Array<{
        dayOfTheWeek: Types.DayOfTheWeek;
        start: string;
        end: string;
      }> | null;
    }> | null;
  } | null;
};

export type GetBaseMenusQueryVariables = Exact<{
  location: string;
  dispatchOption?: Types.FulfillmentType | null | undefined;
}>;

export type GetBaseMenusQuery = {
  ordering: {
    menus: Array<{
      id: string;
      title: string;
      fulfillmentType: Array<Types.FulfillmentType> | null;
      categories: Array<{
        id: string;
        title: string;
        products: Array<{
          id: string;
          plu: string | null;
          title: string;
          description: string | null;
          imageUrl: string | null;
          available: boolean;
          tags: Array<string>;
          visibility: Array<{
            start: string;
            end: string;
            dayOfTheWeek: Types.DayOfTheWeek;
          }> | null;
          price: { amount: number; taxIncluded: number | null };
        }> | null;
      }> | null;
      serviceAvailability: Array<{
        dayOfTheWeek: Types.DayOfTheWeek;
        start: string;
        end: string;
      }> | null;
    }> | null;
  } | null;
};

export type GetOrderingQueryVariables = Exact<{
  location: string;
}>;

export type GetOrderingQuery = {
  ordering: {
    deliveryConfig: {
      active: boolean;
      minutesMin: number;
      minutesMax: number;
      allowItemNotes: boolean;
      allowOrderNotes: boolean;
      orderNotesMaxLength: number | null;
      itemNotesMaxLength: number | null;
      tipsEnabled: boolean;
      tipRatios: Array<number>;
      tipDefaultIndex: number | null;
      externalPaymentEnabled: boolean | null;
      externalPaymentLimit: number | null;
      timeSlots: Array<{
        startTime: string;
        available: boolean;
        ref: string;
      }> | null;
    } | null;
    pickupConfig: {
      active: boolean;
      waitTime: number;
      allowItemNotes: boolean;
      allowOrderNotes: boolean;
      orderNotesMaxLength: number | null;
      itemNotesMaxLength: number | null;
      tipsEnabled: boolean;
      tipRatios: Array<number>;
      tipDefaultIndex: number | null;
      externalPaymentEnabled: boolean | null;
      externalPaymentLimit: number | null;
      timeSlots: Array<{
        startTime: string;
        available: boolean;
        ref: string;
      }> | null;
    } | null;
    serviceConfig: {
      waitTime: number;
      active: boolean;
      serviceChargeLabel: string | null;
      serviceChargeRate: number | null;
      allowItemNotes: boolean;
      allowOrderNotes: boolean;
      orderNotesMaxLength: number | null;
      itemNotesMaxLength: number | null;
      tipsEnabled: boolean;
      tipRatios: Array<number>;
      tipDefaultIndex: number | null;
      externalPaymentEnabled: boolean | null;
      externalPaymentLimit: number | null;
      validRefs: Array<{ ref: string }> | null;
    } | null;
  } | null;
};

export type GetDynamicDeliveryQueryVariables = Exact<{
  location: string;
  to?: Types.CoordsInput | null | undefined;
}>;

export type GetDynamicDeliveryQuery = {
  ordering: {
    deliveryConfig: {
      minimumAmount: number;
      feeAmount: number;
      feePrice: { amount: number; taxIncluded: number | null } | null;
    } | null;
  } | null;
};

export type CheckDeliveryAddressQueryVariables = Exact<{
  location: string;
  address: Types.DeliveryAddress;
}>;

export type CheckDeliveryAddressQuery = {
  ordering: {
    deliveryAddressStatus: { valid: boolean; messages: Array<string> } | null;
  } | null;
};

export type GetRecentCartsQueryVariables = Exact<{
  location: string;
  limit: number;
}>;

export type GetRecentCartsQuery = {
  ordering: {
    recentCarts: Array<{
      locationRef: string;
      locationId: string;
      fulfillmentType: Types.FulfillmentType | null;
      notes: string | null;
      timestamp: string;
      items: Array<{
        id: string;
        quantity: number;
        note: string | null;
        options: Array<{
          id: string;
          choices: Array<{
            id: string;
            quantity: number;
            options: Array<{
              id: string;
              choices: Array<{
                id: string;
                quantity: number;
                options: Array<{
                  id: string;
                  choices: Array<{
                    id: string;
                    quantity: number;
                    options: Array<{
                      id: string;
                      choices: Array<{
                        id: string;
                        quantity: number;
                        options: Array<{
                          id: string;
                          choices: Array<{
                            id: string;
                            quantity: number;
                            options: Array<{
                              id: string;
                              choices: Array<{
                                id: string;
                                quantity: number;
                                options: Array<{
                                  id: string;
                                  choices: Array<{
                                    id: string;
                                    quantity: number;
                                  }>;
                                }>;
                              }>;
                            }>;
                          }>;
                        }>;
                      }>;
                    }>;
                  }>;
                }>;
              }>;
            }>;
          }>;
        }>;
      }>;
    }>;
  } | null;
};

export type GetPayRequirementsQueryVariables = Exact<{ [key: string]: never }>;

export type GetPayRequirementsQuery = {
  merchant: { settings: { requirePhoneWeb: boolean } };
  me: {
    firstName: string;
    lastName: string;
    emailAddress: string | null;
    phoneNumber: string | null;
    dob: string | null;
    hasAgreedToTerms: boolean;
    hasVerifiedPayments: boolean;
  } | null;
};

export type GetPaymentMethodsQueryVariables = Exact<{ [key: string]: never }>;

export type GetPaymentMethodsQuery = {
  me: {
    paymentMethods: Array<{
      id: string;
      brand: string | null;
      last4: string | null;
      isExpired: boolean;
    }>;
  } | null;
};

export type RemovePaymentMethodMutationVariables = Exact<{
  paymentMethod: string;
}>;

export type RemovePaymentMethodMutation = {
  removePaymentMethodV2: { error: { message: string } | null };
};

export type CreateSetupIntentMutationVariables = Exact<{
  [key: string]: never;
}>;

export type CreateSetupIntentMutation = {
  createSetupIntent: {
    error: { message: string } | null;
    intent: { clientSecret: string } | null;
  };
};

export type GetPaymentQueryVariables = Exact<{
  paymentId: string;
}>;

export type GetPaymentQuery = {
  payment: {
    state: Types.PaymentState;
    session: {
      id: string;
      paymentIntentSecret: string | null;
      currency: string;
      creditAmount: number;
      discountAmount: number;
      paymentAmount: number;
      requirePhoneVerification: boolean | null;
      tipAmount: number;
      summaryItems: Array<{
        label: string;
        summaryItemType: Types.SummaryItemType;
        amount: number;
        taxIncluded: number | null;
      }>;
    } | null;
  } | null;
};

export type GetTransactionQueryVariables = Exact<{
  paymentId: string;
}>;

export type GetTransactionQuery = {
  order: {
    points: number | null;
    created: string;
    fulfillAt: string | null;
    estimatedReadyAt: string | null;
    deliveryAtMin: string | null;
    deliveryAtMax: string | null;
    total: number;
    tax: number | null;
    itemTotal: number;
    itemTax: number | null;
    billingType: string | null;
    tipAmount: number | null;
    creditAmount: number;
    customerFeeAmount: number;
    externalAmount: number;
    currency: string;
    paymentMethodType: string | null;
    paymentMethodName: string | null;
    paymentMethodIconUrl: string | null;
    serviceRef: string | null;
    orderAcceptedAt: string | null;
    location: { id: string; slug: string | null; name: string } | null;
    event: { tone: Types.Tone; message: string; timestamp: string };
    items: Array<{
      amount: number;
      tax: number | null;
      name: string;
      quantity: number;
      options: Array<{
        name: string;
        choices: Array<{
          amount: number;
          name: string;
          tax: number | null;
        } | null>;
      } | null> | null;
    }>;
    discounts: Array<{
      amount: number;
      tax: number | null;
      name: string;
    } | null> | null;
  } | null;
};

export type RequestPhoneVerificationMutationVariables = Exact<{
  phoneNumber: string;
}>;

export type RequestPhoneVerificationMutation = {
  requestPhoneNumberVerification: { error: { message: string } | null };
};

export type VerifyPhoneMutationVariables = Exact<{
  code: string;
}>;

export type VerifyPhoneMutation = {
  verifyPhoneNumber: { error: { message: string; code: string | null } | null };
};

export type GetPriorityMessagesQueryVariables = Exact<{
  locationRef: string;
}>;

export type GetPriorityMessagesQuery = {
  priorityMessages: Array<{
    messageId: string;
    source: string;
    header: string;
    body: string;
    flags: Array<string>;
    isGlobal: boolean;
    updatedAt: string;
  }>;
};

export type GetUserProfileFieldsQueryVariables = Exact<{
  scope: Types.ProfileScope;
}>;

export type GetUserProfileFieldsQuery = {
  profileFields: Array<{
    label: string | null;
    formattedLabel: string | null;
    field: string | null;
    value: string | null;
    type: Types.ProfileItemType | null;
    required: boolean | null;
  }> | null;
};

export type GetPromotionsHeaderQueryVariables = Exact<{
  locationRef: string;
}>;

export type GetPromotionsHeaderQuery = {
  strings: { noPointsText: string | null } | null;
  balance: {
    pointsText: string | null;
    creditText: string | null;
    credit: number | null;
  };
};

export type GetPromotionsQueryVariables = Exact<{
  locationRef: string;
}>;

export type GetPromotionsQuery = {
  promotions: Array<{
    id: string;
    displayOrder: number;
    title: string;
    badgeText: string;
    details: string | null;
    collectionDescription: string | null;
    imageUrl: string | null;
    type: Types.PromotionType;
    priceType: Types.PromotionPriceType;
    rewardType: Types.PromotionRewardType | null;
    claimButtonText: string | null;
    url: string | null;
  }> | null;
};

export type ClaimPromotionMutationVariables = Exact<{
  promotionId: string;
  locationRef: string;
}>;

export type ClaimPromotionMutation = {
  claimPromotion: { error: { message: string } | null };
};

export type StartPromotionRedemptionMutationVariables = Exact<{
  promotionId: string;
  locationRef: string;
}>;

export type StartPromotionRedemptionMutation = {
  startPromotionRedemption: {
    error: { message: string } | null;
    session: {
      id: string;
      paymentIntentSecret: string | null;
      currency: string;
      creditAmount: number;
      discountAmount: number;
      paymentAmount: number;
      requirePhoneVerification: boolean | null;
      summaryItems: Array<{
        label: string;
        summaryItemType: Types.SummaryItemType;
        amount: number;
        taxIncluded: number | null;
      }>;
    } | null;
  };
};

export type CompletePromotionRedemptionMutationVariables = Exact<{
  orderId: string;
  locationRef: string;
}>;

export type CompletePromotionRedemptionMutation = {
  completePromotionRedemption: { error: { message: string } | null };
};

export type CancelPromotionRedemptionMutationVariables = Exact<{
  orderId: string;
}>;

export type CancelPromotionRedemptionMutation = {
  cancelPromotionRedemption: { error: { message: string } | null };
};

export type RewardCreditFieldsFragment = {
  __typename: "RewardCredit";
  id: string;
  amount: number;
  remaining: number;
  expiresAt: string | null;
  title: string;
  locations: Array<{ id: string; name: string }> | null;
};

export type RewardDiscountFieldsFragment = {
  __typename: "RewardDiscount";
  id: string;
  name: string;
  rate: number;
  maxAmount: number | null;
  maxRedemptionsPerCustomer: number | null;
  remainingRedemptionsPerCustomer: number | null;
  startAt: string | null;
  expiresAt: string | null;
  items: Array<{ id: string }> | null;
  locations: Array<{ name: string }> | null;
  allocations: Array<{ id: string; expiresAt: string | null }> | null;
};

export type GetRewardsQueryVariables = Exact<{
  after?: string | null | undefined;
}>;

export type GetRewardsQuery = {
  rewards: {
    items: Array<
      | {
          __typename: "RewardCredit";
          id: string;
          amount: number;
          remaining: number;
          expiresAt: string | null;
          title: string;
          locations: Array<{ id: string; name: string }> | null;
        }
      | {
          __typename: "RewardDiscount";
          id: string;
          name: string;
          rate: number;
          maxAmount: number | null;
          maxRedemptionsPerCustomer: number | null;
          remainingRedemptionsPerCustomer: number | null;
          startAt: string | null;
          expiresAt: string | null;
          items: Array<{ id: string }> | null;
          locations: Array<{ name: string }> | null;
          allocations: Array<{ id: string; expiresAt: string | null }> | null;
        }
    >;
    pageInfo: {
      hasNextPage: boolean;
      hasPreviousPage: boolean;
      startCursor: string;
      endCursor: string;
    } | null;
  } | null;
};

export type GetRewardQueryVariables = Exact<{
  id: string;
}>;

export type GetRewardQuery = {
  reward:
    | {
        __typename: "RewardCredit";
        id: string;
        amount: number;
        remaining: number;
        expiresAt: string | null;
        title: string;
        locations: Array<{ id: string; name: string }> | null;
      }
    | {
        __typename: "RewardDiscount";
        id: string;
        name: string;
        rate: number;
        maxAmount: number | null;
        maxRedemptionsPerCustomer: number | null;
        remainingRedemptionsPerCustomer: number | null;
        startAt: string | null;
        expiresAt: string | null;
        items: Array<{ id: string }> | null;
        locations: Array<{ name: string }> | null;
        allocations: Array<{ id: string; expiresAt: string | null }> | null;
      }
    | null;
};

export type GetStripeQueryVariables = Exact<{ [key: string]: never }>;

export type GetStripeQuery = {
  merchant: { stripePublishableKey: string | null };
};

export type SubmitUpsellSuggestionMutationVariables = Exact<{
  location: string;
  cart: Types.UpsellCartInput;
  fulfillmentType?: Types.FulfillmentType | null | undefined;
}>;

export type SubmitUpsellSuggestionMutation = {
  submitUpsellSuggestion: {
    requestId: string | null;
    provider: string | null;
    error: { message: string } | null;
  };
};

export type GetUpsellSuggestionsQueryVariables = Exact<{
  requestId: string;
}>;

export type GetUpsellSuggestionsQuery = {
  upsellSuggestions: {
    suggestions: Array<{ productId: string }> | null;
  } | null;
};

export type GetCurrentUserQueryVariables = Exact<{ [key: string]: never }>;

export type GetCurrentUserQuery = {
  me: {
    firstName: string;
    lastName: string;
    emailAddress: string | null;
    phoneNumber: string | null;
    dob: string | null;
    isPaymentsVerified: boolean;
    hasAgreedToTerms: boolean;
  } | null;
  session: { verified: string | null; guest: boolean } | null;
};

export type GetWebLinksQueryVariables = Exact<{
  locationRef: string;
}>;

export type GetWebLinksQuery = {
  webLinks: Array<{ text: string; href: string }>;
};

export const BaseProductDefFieldsFragmentDoc = gql`
    fragment BaseProductDefFields on ProductDef {
  id
  title
}
    `;
export const BaseProductOptionDefFieldsFragmentDoc = gql`
    fragment BaseProductOptionDefFields on ProductOptionDef {
  id
  title
  maxQuantity
  minQuantity
}
    `;
export const BaseOptionChoiceDefFieldsFragmentDoc = gql`
    fragment BaseOptionChoiceDefFields on OptionChoiceDef {
  defaultQuantity
  maxQuantity
  minQuantity
}
    `;
export const BaseProductFieldsFragmentDoc = gql`
    fragment BaseProductFields on ProductV2 {
  id
  plu
  title
  description
  imageUrl
  available
  visibility {
    start
    end
    dayOfTheWeek
  }
  price {
    amount
    taxIncluded
  }
  tags
}
    `;
export const DeepProductFieldsFragmentDoc = gql`
    fragment DeepProductFields on ProductV2 {
  ...BaseProductFields
  options {
    id
    title
    minQuantity
    maxQuantity
    choices {
      minQuantity
      maxQuantity
      defaultQuantity
      product {
        ...BaseProductFields
        options {
          id
          title
          minQuantity
          maxQuantity
          choices {
            minQuantity
            maxQuantity
            defaultQuantity
            product {
              ...BaseProductFields
              options {
                id
                title
                minQuantity
                maxQuantity
                choices {
                  minQuantity
                  maxQuantity
                  defaultQuantity
                  product {
                    ...BaseProductFields
                    options {
                      id
                      title
                      minQuantity
                      maxQuantity
                      choices {
                        minQuantity
                        maxQuantity
                        defaultQuantity
                        product {
                          ...BaseProductFields
                          options {
                            id
                            title
                            minQuantity
                            maxQuantity
                            choices {
                              minQuantity
                              maxQuantity
                              defaultQuantity
                              product {
                                ...BaseProductFields
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}
    ${BaseProductFieldsFragmentDoc}`;
export const RewardCreditFieldsFragmentDoc = gql`
    fragment RewardCreditFields on RewardCredit {
  id
  amount
  remaining
  expiresAt
  title
  locations {
    id
    name
  }
  __typename
}
    `;
export const RewardDiscountFieldsFragmentDoc = gql`
    fragment RewardDiscountFields on RewardDiscount {
  id
  name
  rate
  maxAmount
  items {
    id
  }
  locations {
    name
  }
  allocations {
    id
    expiresAt
  }
  maxRedemptionsPerCustomer
  remainingRedemptionsPerCustomer
  startAt
  expiresAt
  __typename
}
    `;
export const GetActivityDocument = gql`
    query GetActivity($after: String) {
  activity(first: 10, after: $after) {
    pageInfo {
      hasPreviousPage
      hasNextPage
      startCursor
      endCursor
    }
    items {
      __typename
      id
      title
      timestamp
      locationName
      ... on MessageActivity {
        body
      }
      ... on ItemExpiredActivity {
        item {
          id
          name
          expires
          claimed
          claimedLocation
          imageUrl
        }
      }
      ... on PromotionActivity {
        promotion {
          title
          details
          price
          priceType
        }
        promotionItem: item {
          name
          expires
          claimed
          claimedLocation
          imageUrl
        }
        promotionOrder: order {
          event {
            tone
            message
          }
          total
          tax
          itemTotal
          itemTax
          refId
          id
          billingType
          tipAmount
          creditAmount
          customerFeeAmount
          currency
          deliveryAddress {
            apartment
            streetAddress
            locality
            region
            country
            postcode
            coords {
              lat
              lng
            }
          }
          serviceRef
          items {
            amount
            tax
            name
            quantity
            options {
              name
              choices {
                amount
                name
                tax
              }
            }
          }
          discounts {
            amount
            tax
            name
          }
        }
        promotionCredit: credit {
          id
          title
          amount
          remaining
          created
          expires
        }
        points
        pointsText
      }
      ... on CreditExpiredActivity {
        credit {
          id
          title
          amount
          remaining
          created
          expires
        }
      }
      ... on PointsActivity {
        points
        pointsText
      }
      ... on SpendActivity {
        pointsText
        points
        order {
          event {
            tone
            message
          }
          total
          tax
          itemTotal
          itemTax
          refId
          id
          billingType
          tipAmount
          creditAmount
          customerFeeAmount
          externalAmount
          currency
          paymentId
          paymentMethodType
          paymentMethodName
          paymentMethodIconUrl
          deliveryAddress {
            apartment
            streetAddress
            locality
            region
            country
            postcode
            coords {
              lat
              lng
            }
          }
          serviceRef
          items {
            amount
            tax
            name
            quantity
            options {
              name
              choices {
                amount
                name
                tax
              }
            }
          }
          discounts {
            amount
            tax
            name
          }
          fulfillAt
          estimatedReadyAt
          deliveryAtMin
          deliveryAtMax
        }
        cart {
          locationId
          locationRef
          timestamp
          notes
          items {
            id
            note
            quantity
            options {
              id
              choices {
                id
                quantity
                options {
                  id
                  choices {
                    id
                    quantity
                    options {
                      id
                      choices {
                        id
                        quantity
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}
    `;
export const GetActivitySummaryDocument = gql`
    query GetActivitySummary {
  activity {
    summary {
      points
      credit
      itemCount
    }
  }
}
    `;
export const GetAddressesDocument = gql`
    query GetAddresses($query: String!, $locationRef: String, $sessionToken: String) {
  addressAutocomplete(
    query: $query
    locationRef: $locationRef
    sessionToken: $sessionToken
  ) {
    name
    ref
  }
}
    `;
export const GetAddressDetailsDocument = gql`
    query GetAddressDetails($ref: String!, $sessionToken: String!) {
  addressDetails(ref: $ref, sessionToken: $sessionToken) {
    details {
      name
      streetAddress
      locality
      postcode
      region
      country
      coords {
        lat
        lng
      }
    }
    error {
      message
    }
  }
}
    `;
export const FindPlaceDocument = gql`
    query FindPlace($query: String!) {
  findPlace(query: $query) {
    name
    formattedAddress
    lat
    lng
  }
}
    `;
export const GetPromptAppReviewDocument = gql`
    query GetPromptAppReview {
  promptAppReview
}
    `;
export const SubmitAppReviewDocument = gql`
    mutation SubmitAppReview($score: AppScore!, $deviceId: String) {
  submitAppReview(score: $score, deviceId: $deviceId) {
    error {
      message
    }
  }
}
    `;
export const CreateCartDocument = gql`
    mutation CreateCart($id: String!, $locationRef: String!, $items: [CartItemInput!]!, $fulfillmentType: FulfillmentType!, $notes: String, $deliveryAddress: DeliveryAddress, $serviceRef: String, $timeSlot: String, $tags: [String!]) {
  createCart(
    id: $id
    location: $locationRef
    items: $items
    fulfillmentType: $fulfillmentType
    notes: $notes
    deliveryAddress: $deliveryAddress
    serviceRef: $serviceRef
    timeSlot: $timeSlot
    tags: $tags
  ) {
    adjustments {
      id
      label
      amount
      taxIncluded
      itemsAffected {
        index
        quantity
      }
    }
    discounts {
      id
      name
      discountAmount
      discountTax
      itemId
      rate
    }
    error {
      message
    }
  }
}
    `;
export const RedeemCodeDocument = gql`
    mutation RedeemCode($code: String!) {
  redeemCode(code: $code) {
    error {
      message
    }
    messageDetails {
      title
      message
      ... on RewardMessageDetails {
        rewardType
      }
    }
  }
}
    `;
export const GetCreditDocument = gql`
    query GetCredit($location: String!) {
  balance(location: $location) {
    credit
  }
}
    `;
export const GetLocationDealsDocument = gql`
    query GetLocationDeals($locationRef: String!) {
  locationDeals(locationRef: $locationRef) {
    id
    title
    plu
    hidden
    exclusive
    description
    imageUrl
    startAt
    endAt
    options {
      id
      title
      maxQuantity
      minQuantity
      choices {
        defaultQuantity
        maxQuantity
        minQuantity
        productDef {
          ...BaseProductDefFields
          options {
            ...BaseProductOptionDefFields
            choices {
              ...BaseOptionChoiceDefFields
              productDef {
                ...BaseProductDefFields
                options {
                  ...BaseProductOptionDefFields
                  choices {
                    ...BaseOptionChoiceDefFields
                    productDef {
                      ...BaseProductDefFields
                      options {
                        ...BaseProductOptionDefFields
                        choices {
                          ...BaseOptionChoiceDefFields
                          productDef {
                            ...BaseProductDefFields
                            options {
                              ...BaseProductOptionDefFields
                              choices {
                                ...BaseOptionChoiceDefFields
                                productDef {
                                  ...BaseProductDefFields
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    availability {
      dayOfTheWeek
      start
      end
    }
    fulfillmentType
  }
}
    ${BaseProductDefFieldsFragmentDoc}
${BaseProductOptionDefFieldsFragmentDoc}
${BaseOptionChoiceDefFieldsFragmentDoc}`;
export const GetFeaturesDocument = gql`
    query GetFeatures($keys: [String!]!, $deviceId: String, $location: String) {
  features(keys: $keys, deviceId: $deviceId, location: $location) {
    key
    enabled
  }
}
    `;
export const GetLanguageStringsDocument = gql`
    query GetLanguageStrings {
  strings {
    webOrderPickupName
    webOrderDeliveryName
    webOrderServiceName
    webChooseLocationTitle
    serviceChargeTitle
    serviceChargeBody
    serviceChargeApply
    serviceChargeRemove
    webServiceRefLabel
    webPlaceRefLabel
    webExternalPaymentPickUpButton
    webExternalPaymentPickUpMessage
    webExternalPaymentDeliveryButton
    webExternalPaymentDeliveryMessage
    webExternalPaymentServiceButton
    webExternalPaymentServiceMessage
  }
}
    `;
export const GetLocationOrderingEnabledDocument = gql`
    query GetLocationOrderingEnabled($locationRef: String!) {
  location(locationRef: $locationRef) {
    orderAheadEnabled
    orderAtTableEnabled
    orderDeliveryEnabled
  }
}
    `;
export const GetLocationsDocument = gql`
    query GetLocations {
  merchant {
    name
  }
  locations(nativeOrderingEnabled: true) {
    id
    slug
    name
    phoneNumber
    openingHours
    website
    address {
      street
      locality
      region
      postalCode
      country
    }
    coords {
      latitude: lat
      longitude: lng
    }
    social {
      facebook
      twitter
      instagram
    }
    orderAheadEnabled
    orderAtTableEnabled
    orderDeliveryEnabled
    timezone
  }
}
    `;
export const GetLoginConfigDocument = gql`
    query GetLoginConfig {
  merchant {
    googleClientId
    appleWebClientId
    settings {
      loginProviders
    }
  }
}
    `;
export const GetMerchantDocument = gql`
    query GetMerchant {
  merchant {
    uid
    androidStoreUrl
    iosStoreUrl
    currency
    settings {
      termsAndConditionsURL
    }
  }
}
    `;
export const GetMerchantRequiresPhoneDocument = gql`
    query GetMerchantRequiresPhone {
  merchant {
    settings {
      requirePhoneWeb
    }
  }
}
    `;
export const CreateOrderDocument = gql`
    mutation CreateOrder($id: String!, $locationRef: String!, $total: Int!, $items: [OrderAheadItem!]!, $notes: String, $deliveryAddress: DeliveryAddress, $serviceRef: String, $tip: Int, $timeSlot: String, $customerOverride: OrderCustomerOverride) {
  createOrder(
    id: $id
    location: $locationRef
    total: $total
    items: $items
    deliveryAddress: $deliveryAddress
    notes: $notes
    serviceRef: $serviceRef
    tip: $tip
    timeSlot: $timeSlot
    customerOverride: $customerOverride
  ) {
    session {
      id
      currency
      paymentIntentSecret
      creditAmount
      discountAmount
      paymentAmount
      summaryItems {
        label
        summaryItemType
        amount
        taxIncluded
      }
      requirePhoneVerification
      token
    }
    error {
      message
    }
  }
}
    `;
export const CreateOrderFromCartDocument = gql`
    mutation CreateOrderFromCart($cartId: String!, $locationRef: String!, $tip: Int, $customerOverride: OrderCustomerOverride) {
  createOrderFromCart(
    cartId: $cartId
    location: $locationRef
    tip: $tip
    customerOverride: $customerOverride
  ) {
    session {
      id
      currency
      paymentIntentSecret
      creditAmount
      discountAmount
      paymentAmount
      summaryItems {
        label
        summaryItemType
        amount
        taxIncluded
      }
      requirePhoneVerification
      token
    }
    error {
      message
    }
  }
}
    `;
export const UpdateOrderTipDocument = gql`
    mutation UpdateOrderTip($orderId: String!, $tip: Int!) {
  updateOrderTip(id: $orderId, tip: $tip) {
    error {
      message
    }
  }
}
    `;
export const CompleteOrderDocument = gql`
    mutation CompleteOrder($orderId: String!, $externalPayment: Boolean) {
  completeOrder(id: $orderId, externalPayment: $externalPayment) {
    error {
      message
    }
    newCustomer
  }
}
    `;
export const CancelOrderDocument = gql`
    mutation CancelOrder($orderId: String!) {
  cancelOrder(id: $orderId) {
    error {
      message
    }
  }
}
    `;
export const GetMenusDocument = gql`
    query GetMenus($location: String!, $dispatchOption: FulfillmentType, $productIds: [String!]) {
  ordering(locationRef: $location) {
    menus(fulfillmentType: $dispatchOption) {
      id
      title
      fulfillmentType
      categories {
        id
        title
        products(productIds: $productIds) {
          ...DeepProductFields
        }
      }
      serviceAvailability {
        dayOfTheWeek
        start
        end
      }
    }
  }
}
    ${DeepProductFieldsFragmentDoc}`;
export const GetBaseMenusDocument = gql`
    query GetBaseMenus($location: String!, $dispatchOption: FulfillmentType) {
  ordering(locationRef: $location) {
    menus(fulfillmentType: $dispatchOption) {
      id
      title
      fulfillmentType
      categories {
        id
        title
        products {
          ...BaseProductFields
        }
      }
      serviceAvailability {
        dayOfTheWeek
        start
        end
      }
    }
  }
}
    ${BaseProductFieldsFragmentDoc}`;
export const GetOrderingDocument = gql`
    query GetOrdering($location: String!) {
  ordering(locationRef: $location) {
    deliveryConfig {
      active
      minutesMin
      minutesMax
      allowItemNotes
      allowOrderNotes
      orderNotesMaxLength
      itemNotesMaxLength
      tipsEnabled
      tipRatios
      tipDefaultIndex
      externalPaymentEnabled
      externalPaymentLimit
      timeSlots {
        startTime
        available
        ref
      }
    }
    pickupConfig {
      active
      waitTime
      allowItemNotes
      allowOrderNotes
      orderNotesMaxLength
      itemNotesMaxLength
      tipsEnabled
      tipRatios
      tipDefaultIndex
      timeSlots {
        startTime
        available
        ref
      }
      externalPaymentEnabled
      externalPaymentLimit
    }
    serviceConfig {
      waitTime
      active
      validRefs {
        ref
      }
      serviceChargeLabel
      serviceChargeRate
      allowItemNotes
      allowOrderNotes
      orderNotesMaxLength
      itemNotesMaxLength
      tipsEnabled
      tipRatios
      tipDefaultIndex
      externalPaymentEnabled
      externalPaymentLimit
    }
  }
}
    `;
export const GetDynamicDeliveryDocument = gql`
    query GetDynamicDelivery($location: String!, $to: CoordsInput) {
  ordering(locationRef: $location) {
    deliveryConfig(to: $to) {
      minimumAmount
      feeAmount
      feePrice {
        amount
        taxIncluded
      }
    }
  }
}
    `;
export const CheckDeliveryAddressDocument = gql`
    query CheckDeliveryAddress($location: String!, $address: DeliveryAddress!) {
  ordering(locationRef: $location) {
    deliveryAddressStatus(address: $address) {
      valid
      messages
    }
  }
}
    `;
export const GetRecentCartsDocument = gql`
    query GetRecentCarts($location: String!, $limit: Int!) {
  ordering(locationRef: $location) {
    recentCarts(limit: $limit) {
      locationRef
      locationId
      fulfillmentType
      notes
      timestamp
      items {
        id
        quantity
        note
        options {
          id
          choices {
            id
            quantity
            options {
              id
              choices {
                id
                quantity
                options {
                  id
                  choices {
                    id
                    quantity
                    options {
                      id
                      choices {
                        id
                        quantity
                        options {
                          id
                          choices {
                            id
                            quantity
                            options {
                              id
                              choices {
                                id
                                quantity
                                options {
                                  id
                                  choices {
                                    id
                                    quantity
                                  }
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}
    `;
export const GetPayRequirementsDocument = gql`
    query GetPayRequirements {
  merchant {
    settings {
      requirePhoneWeb
    }
  }
  me {
    firstName
    lastName
    emailAddress
    phoneNumber
    dob
    hasVerifiedPayments: isPaymentsVerified
    hasAgreedToTerms
  }
}
    `;
export const GetPaymentMethodsDocument = gql`
    query GetPaymentMethods {
  me {
    paymentMethods {
      id
      brand
      last4
      isExpired
    }
  }
}
    `;
export const RemovePaymentMethodDocument = gql`
    mutation RemovePaymentMethod($paymentMethod: String!) {
  removePaymentMethodV2(paymentMethod: $paymentMethod) {
    error {
      message
    }
  }
}
    `;
export const CreateSetupIntentDocument = gql`
    mutation CreateSetupIntent {
  createSetupIntent {
    error {
      message
    }
    intent {
      clientSecret
    }
  }
}
    `;
export const GetPaymentDocument = gql`
    query GetPayment($paymentId: String!) {
  payment(paymentId: $paymentId) {
    state
    session: orderSession {
      id
      paymentIntentSecret
      currency
      creditAmount
      discountAmount
      summaryItems {
        label
        summaryItemType
        amount
        taxIncluded
      }
      paymentAmount
      requirePhoneVerification
      tipAmount
    }
  }
}
    `;
export const GetTransactionDocument = gql`
    query GetTransaction($paymentId: String!) {
  order(orderId: $paymentId) {
    points
    location {
      id
      slug
      name
    }
    created
    fulfillAt
    estimatedReadyAt
    deliveryAtMin
    deliveryAtMax
    event {
      tone
      message
      timestamp
    }
    total
    tax
    itemTotal
    itemTax
    billingType
    tipAmount
    creditAmount
    customerFeeAmount
    externalAmount
    currency
    paymentMethodType
    paymentMethodName
    paymentMethodIconUrl
    serviceRef
    orderAcceptedAt
    items {
      amount
      tax
      name
      quantity
      options {
        name
        choices {
          amount
          name
          tax
        }
      }
    }
    discounts {
      amount
      tax
      name
    }
  }
}
    `;
export const RequestPhoneVerificationDocument = gql`
    mutation RequestPhoneVerification($phoneNumber: String!) {
  requestPhoneNumberVerification(phoneNumber: $phoneNumber) {
    error {
      message
    }
  }
}
    `;
export const VerifyPhoneDocument = gql`
    mutation VerifyPhone($code: String!) {
  verifyPhoneNumber(verificationCode: $code) {
    error {
      message
      code
    }
  }
}
    `;
export const GetPriorityMessagesDocument = gql`
    query GetPriorityMessages($locationRef: String!) {
  priorityMessages(locationRef: $locationRef) {
    messageId
    source
    header
    body
    flags
    isGlobal
    updatedAt
  }
}
    `;
export const GetUserProfileFieldsDocument = gql`
    query GetUserProfileFields($scope: ProfileScope!) {
  profileFields(scope: $scope) {
    label
    formattedLabel
    field
    value
    type
    required
  }
}
    `;
export const GetPromotionsHeaderDocument = gql`
    query GetPromotionsHeader($locationRef: String!) {
  strings {
    noPointsText
  }
  balance(location: $locationRef) {
    pointsText
    creditText
    credit
  }
}
    `;
export const GetPromotionsDocument = gql`
    query GetPromotions($locationRef: String!) {
  promotions(locationId: $locationRef) {
    id
    displayOrder
    title
    badgeText
    details
    collectionDescription
    imageUrl
    type
    priceType
    rewardType
    claimButtonText
    url
  }
}
    `;
export const ClaimPromotionDocument = gql`
    mutation ClaimPromotion($promotionId: String!, $locationRef: String!) {
  claimPromotion(promotionId: $promotionId, locationId: $locationRef) {
    error {
      message
    }
  }
}
    `;
export const StartPromotionRedemptionDocument = gql`
    mutation StartPromotionRedemption($promotionId: String!, $locationRef: String!) {
  startPromotionRedemption(promotionId: $promotionId, locationId: $locationRef) {
    error {
      message
    }
    session {
      id
      paymentIntentSecret
      currency
      creditAmount
      discountAmount
      paymentAmount
      summaryItems {
        label
        summaryItemType
        amount
        taxIncluded
      }
      requirePhoneVerification
    }
  }
}
    `;
export const CompletePromotionRedemptionDocument = gql`
    mutation CompletePromotionRedemption($orderId: String!, $locationRef: String!) {
  completePromotionRedemption(orderId: $orderId, locationId: $locationRef) {
    error {
      message
    }
  }
}
    `;
export const CancelPromotionRedemptionDocument = gql`
    mutation CancelPromotionRedemption($orderId: String!) {
  cancelPromotionRedemption(orderId: $orderId) {
    error {
      message
    }
  }
}
    `;
export const GetRewardsDocument = gql`
    query GetRewards($after: String) {
  rewards(limit: 100, after: $after) {
    items {
      ...RewardCreditFields
      ...RewardDiscountFields
    }
    pageInfo {
      hasNextPage
      hasPreviousPage
      startCursor
      endCursor
    }
  }
}
    ${RewardCreditFieldsFragmentDoc}
${RewardDiscountFieldsFragmentDoc}`;
export const GetRewardDocument = gql`
    query GetReward($id: String!) {
  reward(id: $id) {
    ...RewardCreditFields
    ...RewardDiscountFields
  }
}
    ${RewardCreditFieldsFragmentDoc}
${RewardDiscountFieldsFragmentDoc}`;
export const GetStripeDocument = gql`
    query GetStripe {
  merchant {
    stripePublishableKey
  }
}
    `;
export const SubmitUpsellSuggestionDocument = gql`
    mutation SubmitUpsellSuggestion($location: String!, $cart: UpsellCartInput!, $fulfillmentType: FulfillmentType) {
  submitUpsellSuggestion(
    location: $location
    cart: $cart
    fulfillmentType: $fulfillmentType
  ) {
    requestId
    provider
    error {
      message
    }
  }
}
    `;
export const GetUpsellSuggestionsDocument = gql`
    query GetUpsellSuggestions($requestId: String!) {
  upsellSuggestions(requestId: $requestId) {
    suggestions {
      productId
    }
  }
}
    `;
export const GetCurrentUserDocument = gql`
    query GetCurrentUser {
  me {
    firstName
    lastName
    emailAddress
    phoneNumber
    dob
    isPaymentsVerified
    hasAgreedToTerms
  }
  session {
    verified
    guest
  }
}
    `;
export const GetWebLinksDocument = gql`
    query GetWebLinks($locationRef: String!) {
  webLinks(locationRef: $locationRef) {
    text
    href
  }
}
    `;

export type SdkFunctionWrapper = <T>(
  action: (requestHeaders?: Record<string, string>) => Promise<T>,
  operationName: string,
  operationType?: string,
  variables?: any,
) => Promise<T>;

const defaultWrapper: SdkFunctionWrapper = (
  action,
  _operationName,
  _operationType,
  _variables,
) => action();

export function getSdk(
  client: GraphQLClient,
  withWrapper: SdkFunctionWrapper = defaultWrapper,
) {
  return {
    GetActivity(
      variables?: GetActivityQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<GetActivityQuery> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<GetActivityQuery>({
            document: GetActivityDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetActivity",
        "query",
        variables,
      );
    },
    GetActivitySummary(
      variables?: GetActivitySummaryQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<GetActivitySummaryQuery> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<GetActivitySummaryQuery>({
            document: GetActivitySummaryDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetActivitySummary",
        "query",
        variables,
      );
    },
    GetAddresses(
      variables: GetAddressesQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<GetAddressesQuery> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<GetAddressesQuery>({
            document: GetAddressesDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetAddresses",
        "query",
        variables,
      );
    },
    GetAddressDetails(
      variables: GetAddressDetailsQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<GetAddressDetailsQuery> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<GetAddressDetailsQuery>({
            document: GetAddressDetailsDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetAddressDetails",
        "query",
        variables,
      );
    },
    FindPlace(
      variables: FindPlaceQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<FindPlaceQuery> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<FindPlaceQuery>({
            document: FindPlaceDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "FindPlace",
        "query",
        variables,
      );
    },
    GetPromptAppReview(
      variables?: GetPromptAppReviewQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<GetPromptAppReviewQuery> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<GetPromptAppReviewQuery>({
            document: GetPromptAppReviewDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetPromptAppReview",
        "query",
        variables,
      );
    },
    SubmitAppReview(
      variables: SubmitAppReviewMutationVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<SubmitAppReviewMutation> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<SubmitAppReviewMutation>({
            document: SubmitAppReviewDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "SubmitAppReview",
        "mutation",
        variables,
      );
    },
    CreateCart(
      variables: CreateCartMutationVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<CreateCartMutation> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<CreateCartMutation>({
            document: CreateCartDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "CreateCart",
        "mutation",
        variables,
      );
    },
    RedeemCode(
      variables: RedeemCodeMutationVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<RedeemCodeMutation> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<RedeemCodeMutation>({
            document: RedeemCodeDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "RedeemCode",
        "mutation",
        variables,
      );
    },
    GetCredit(
      variables: GetCreditQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<GetCreditQuery> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<GetCreditQuery>({
            document: GetCreditDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetCredit",
        "query",
        variables,
      );
    },
    GetLocationDeals(
      variables: GetLocationDealsQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<GetLocationDealsQuery> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<GetLocationDealsQuery>({
            document: GetLocationDealsDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetLocationDeals",
        "query",
        variables,
      );
    },
    GetFeatures(
      variables: GetFeaturesQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<GetFeaturesQuery> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<GetFeaturesQuery>({
            document: GetFeaturesDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetFeatures",
        "query",
        variables,
      );
    },
    GetLanguageStrings(
      variables?: GetLanguageStringsQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<GetLanguageStringsQuery> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<GetLanguageStringsQuery>({
            document: GetLanguageStringsDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetLanguageStrings",
        "query",
        variables,
      );
    },
    GetLocationOrderingEnabled(
      variables: GetLocationOrderingEnabledQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<GetLocationOrderingEnabledQuery> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<GetLocationOrderingEnabledQuery>({
            document: GetLocationOrderingEnabledDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetLocationOrderingEnabled",
        "query",
        variables,
      );
    },
    GetLocations(
      variables?: GetLocationsQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<GetLocationsQuery> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<GetLocationsQuery>({
            document: GetLocationsDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetLocations",
        "query",
        variables,
      );
    },
    GetLoginConfig(
      variables?: GetLoginConfigQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<GetLoginConfigQuery> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<GetLoginConfigQuery>({
            document: GetLoginConfigDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetLoginConfig",
        "query",
        variables,
      );
    },
    GetMerchant(
      variables?: GetMerchantQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<GetMerchantQuery> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<GetMerchantQuery>({
            document: GetMerchantDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetMerchant",
        "query",
        variables,
      );
    },
    GetMerchantRequiresPhone(
      variables?: GetMerchantRequiresPhoneQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<GetMerchantRequiresPhoneQuery> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<GetMerchantRequiresPhoneQuery>({
            document: GetMerchantRequiresPhoneDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetMerchantRequiresPhone",
        "query",
        variables,
      );
    },
    CreateOrder(
      variables: CreateOrderMutationVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<CreateOrderMutation> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<CreateOrderMutation>({
            document: CreateOrderDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "CreateOrder",
        "mutation",
        variables,
      );
    },
    CreateOrderFromCart(
      variables: CreateOrderFromCartMutationVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<CreateOrderFromCartMutation> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<CreateOrderFromCartMutation>({
            document: CreateOrderFromCartDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "CreateOrderFromCart",
        "mutation",
        variables,
      );
    },
    UpdateOrderTip(
      variables: UpdateOrderTipMutationVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<UpdateOrderTipMutation> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<UpdateOrderTipMutation>({
            document: UpdateOrderTipDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "UpdateOrderTip",
        "mutation",
        variables,
      );
    },
    CompleteOrder(
      variables: CompleteOrderMutationVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<CompleteOrderMutation> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<CompleteOrderMutation>({
            document: CompleteOrderDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "CompleteOrder",
        "mutation",
        variables,
      );
    },
    CancelOrder(
      variables: CancelOrderMutationVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<CancelOrderMutation> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<CancelOrderMutation>({
            document: CancelOrderDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "CancelOrder",
        "mutation",
        variables,
      );
    },
    GetMenus(
      variables: GetMenusQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<GetMenusQuery> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<GetMenusQuery>({
            document: GetMenusDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetMenus",
        "query",
        variables,
      );
    },
    GetBaseMenus(
      variables: GetBaseMenusQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<GetBaseMenusQuery> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<GetBaseMenusQuery>({
            document: GetBaseMenusDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetBaseMenus",
        "query",
        variables,
      );
    },
    GetOrdering(
      variables: GetOrderingQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<GetOrderingQuery> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<GetOrderingQuery>({
            document: GetOrderingDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetOrdering",
        "query",
        variables,
      );
    },
    GetDynamicDelivery(
      variables: GetDynamicDeliveryQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<GetDynamicDeliveryQuery> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<GetDynamicDeliveryQuery>({
            document: GetDynamicDeliveryDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetDynamicDelivery",
        "query",
        variables,
      );
    },
    CheckDeliveryAddress(
      variables: CheckDeliveryAddressQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<CheckDeliveryAddressQuery> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<CheckDeliveryAddressQuery>({
            document: CheckDeliveryAddressDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "CheckDeliveryAddress",
        "query",
        variables,
      );
    },
    GetRecentCarts(
      variables: GetRecentCartsQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<GetRecentCartsQuery> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<GetRecentCartsQuery>({
            document: GetRecentCartsDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetRecentCarts",
        "query",
        variables,
      );
    },
    GetPayRequirements(
      variables?: GetPayRequirementsQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<GetPayRequirementsQuery> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<GetPayRequirementsQuery>({
            document: GetPayRequirementsDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetPayRequirements",
        "query",
        variables,
      );
    },
    GetPaymentMethods(
      variables?: GetPaymentMethodsQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<GetPaymentMethodsQuery> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<GetPaymentMethodsQuery>({
            document: GetPaymentMethodsDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetPaymentMethods",
        "query",
        variables,
      );
    },
    RemovePaymentMethod(
      variables: RemovePaymentMethodMutationVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<RemovePaymentMethodMutation> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<RemovePaymentMethodMutation>({
            document: RemovePaymentMethodDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "RemovePaymentMethod",
        "mutation",
        variables,
      );
    },
    CreateSetupIntent(
      variables?: CreateSetupIntentMutationVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<CreateSetupIntentMutation> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<CreateSetupIntentMutation>({
            document: CreateSetupIntentDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "CreateSetupIntent",
        "mutation",
        variables,
      );
    },
    GetPayment(
      variables: GetPaymentQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<GetPaymentQuery> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<GetPaymentQuery>({
            document: GetPaymentDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetPayment",
        "query",
        variables,
      );
    },
    GetTransaction(
      variables: GetTransactionQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<GetTransactionQuery> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<GetTransactionQuery>({
            document: GetTransactionDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetTransaction",
        "query",
        variables,
      );
    },
    RequestPhoneVerification(
      variables: RequestPhoneVerificationMutationVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<RequestPhoneVerificationMutation> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<RequestPhoneVerificationMutation>({
            document: RequestPhoneVerificationDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "RequestPhoneVerification",
        "mutation",
        variables,
      );
    },
    VerifyPhone(
      variables: VerifyPhoneMutationVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<VerifyPhoneMutation> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<VerifyPhoneMutation>({
            document: VerifyPhoneDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "VerifyPhone",
        "mutation",
        variables,
      );
    },
    GetPriorityMessages(
      variables: GetPriorityMessagesQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<GetPriorityMessagesQuery> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<GetPriorityMessagesQuery>({
            document: GetPriorityMessagesDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetPriorityMessages",
        "query",
        variables,
      );
    },
    GetUserProfileFields(
      variables: GetUserProfileFieldsQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<GetUserProfileFieldsQuery> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<GetUserProfileFieldsQuery>({
            document: GetUserProfileFieldsDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetUserProfileFields",
        "query",
        variables,
      );
    },
    GetPromotionsHeader(
      variables: GetPromotionsHeaderQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<GetPromotionsHeaderQuery> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<GetPromotionsHeaderQuery>({
            document: GetPromotionsHeaderDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetPromotionsHeader",
        "query",
        variables,
      );
    },
    GetPromotions(
      variables: GetPromotionsQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<GetPromotionsQuery> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<GetPromotionsQuery>({
            document: GetPromotionsDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetPromotions",
        "query",
        variables,
      );
    },
    ClaimPromotion(
      variables: ClaimPromotionMutationVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<ClaimPromotionMutation> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<ClaimPromotionMutation>({
            document: ClaimPromotionDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "ClaimPromotion",
        "mutation",
        variables,
      );
    },
    StartPromotionRedemption(
      variables: StartPromotionRedemptionMutationVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<StartPromotionRedemptionMutation> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<StartPromotionRedemptionMutation>({
            document: StartPromotionRedemptionDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "StartPromotionRedemption",
        "mutation",
        variables,
      );
    },
    CompletePromotionRedemption(
      variables: CompletePromotionRedemptionMutationVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<CompletePromotionRedemptionMutation> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<CompletePromotionRedemptionMutation>({
            document: CompletePromotionRedemptionDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "CompletePromotionRedemption",
        "mutation",
        variables,
      );
    },
    CancelPromotionRedemption(
      variables: CancelPromotionRedemptionMutationVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<CancelPromotionRedemptionMutation> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<CancelPromotionRedemptionMutation>({
            document: CancelPromotionRedemptionDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "CancelPromotionRedemption",
        "mutation",
        variables,
      );
    },
    GetRewards(
      variables?: GetRewardsQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<GetRewardsQuery> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<GetRewardsQuery>({
            document: GetRewardsDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetRewards",
        "query",
        variables,
      );
    },
    GetReward(
      variables: GetRewardQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<GetRewardQuery> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<GetRewardQuery>({
            document: GetRewardDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetReward",
        "query",
        variables,
      );
    },
    GetStripe(
      variables?: GetStripeQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<GetStripeQuery> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<GetStripeQuery>({
            document: GetStripeDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetStripe",
        "query",
        variables,
      );
    },
    SubmitUpsellSuggestion(
      variables: SubmitUpsellSuggestionMutationVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<SubmitUpsellSuggestionMutation> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<SubmitUpsellSuggestionMutation>({
            document: SubmitUpsellSuggestionDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "SubmitUpsellSuggestion",
        "mutation",
        variables,
      );
    },
    GetUpsellSuggestions(
      variables: GetUpsellSuggestionsQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<GetUpsellSuggestionsQuery> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<GetUpsellSuggestionsQuery>({
            document: GetUpsellSuggestionsDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetUpsellSuggestions",
        "query",
        variables,
      );
    },
    GetCurrentUser(
      variables?: GetCurrentUserQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<GetCurrentUserQuery> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<GetCurrentUserQuery>({
            document: GetCurrentUserDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetCurrentUser",
        "query",
        variables,
      );
    },
    GetWebLinks(
      variables: GetWebLinksQueryVariables,
      requestHeaders?: GraphQLClientRequestHeaders,
      signal?: RequestInit["signal"],
    ): Promise<GetWebLinksQuery> {
      return withWrapper(
        (wrappedRequestHeaders) =>
          client.request<GetWebLinksQuery>({
            document: GetWebLinksDocument,
            variables,
            requestHeaders: { ...requestHeaders, ...wrappedRequestHeaders },
            signal,
          }),
        "GetWebLinks",
        "query",
        variables,
      );
    },
  };
}
export type Sdk = ReturnType<typeof getSdk>;
